import { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import * as THREE from "three";
import { STLLoader } from "../lib/stl/STLLoader.js";
import { OBJLoader } from "three/addons/loaders/OBJLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { MapControls } from "../lib/stl/OrbitControls.js";

interface Props {
    model: string | undefined;
    width: string;
    height: string;
    // CAS URLs are extensionless (/api/cas/{hash}), so the format can't be
    // inferred from `model` — the carousel passes it explicitly. Defaults keep
    // the old .stl/.obj-by-extension behaviour for any caller that omits it.
    format?: "glb" | "obj" | "stl";
    onProgress?: (percent: number) => void;
}

export function StlViewer({ model, width, height, format, onProgress }: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const controlsRef = useRef<any>(null);
    const currentMeshRef = useRef<THREE.Mesh | null>(null);
    const frameRef = useRef<number | null>(null);
    const loadTokenRef = useRef(0);
    const [progress, setProgress] = useState(0);

    // One-time Three.js initialization and teardown.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.style.width = width;
        container.style.height = height;

        const camera = new THREE.PerspectiveCamera(
            50,
            container.clientWidth / container.clientHeight,
            1,
            1000,
        );
        camera.up = new THREE.Vector3(0, 0, 1);
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(container.clientWidth, container.clientHeight);
        rendererRef.current = renderer;

        const controls = new MapControls(camera, renderer.domElement);
        controls.enableZoom = true;
        controlsRef.current = controls;

        const scene = new THREE.Scene();
        const sun = new THREE.DirectionalLight(0xe0e0ff, 1.5);
        sun.position.set(-1, 1, 1);
        scene.add(sun);
        const scatter = new THREE.HemisphereLight(0xe0e0ff, 0xffffff, 0.2);
        scatter.position.set(1, -1, 1);
        scene.add(scatter);
        sceneRef.current = scene;

        const onResize = () => {
            if (!rendererRef.current || !cameraRef.current || !containerRef.current) return;
            rendererRef.current.setSize(
                containerRef.current.clientWidth,
                containerRef.current.clientHeight,
            );
            cameraRef.current.aspect =
                containerRef.current.clientWidth / containerRef.current.clientHeight;
            cameraRef.current.updateProjectionMatrix();
        };
        window.addEventListener("resize", onResize);

        container.innerHTML = "";
        container.appendChild(renderer.domElement);

        const animate = () => {
            controls.update();
            renderer.render(scene, camera);
            frameRef.current = requestAnimationFrame(animate);
        };
        frameRef.current = requestAnimationFrame(animate);

        return () => {
            window.removeEventListener("resize", onResize);
            if (frameRef.current !== null) {
                cancelAnimationFrame(frameRef.current);
                frameRef.current = null;
            }
            controls.dispose?.();
            if (currentMeshRef.current) {
                scene.remove(currentMeshRef.current);
                currentMeshRef.current.geometry?.dispose?.();
                currentMeshRef.current = null;
            }
            renderer.dispose();
            if (renderer.domElement.parentNode === container) {
                container.removeChild(renderer.domElement);
            }
            rendererRef.current = null;
            sceneRef.current = null;
            cameraRef.current = null;
            controlsRef.current = null;
        };
    }, [width, height]);

    // Load a new model whenever `model` changes.
    useEffect(() => {
        if (!model) return;
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        const controls = controlsRef.current;
        if (!scene || !camera || !controls) return;

        const thisToken = ++loadTokenRef.current;
        setProgress(0);
        onProgress?.(0);

        const fmt = format ?? (model.endsWith(".stl") ? "stl" : "obj");
        const LoaderClass =
            fmt === "glb" ? GLTFLoader : fmt === "stl" ? STLLoader : OBJLoader;
        // STLLoader wraps geometry in a Mesh; OBJLoader yields a Group;
        // GLTFLoader hands back a { scene } wrapper — unwrap to the scene Group.
        // All three end up as a THREE.Object3D with scale/rotation/position.
        const loader = new LoaderClass();
        (loader.load as (url: string, a: (m: any) => void, b?: (e: ProgressEvent) => void, c?: (err: unknown) => void) => void)(
            model,
            (loaded: any) => {
                if (thisToken !== loadTokenRef.current) return;
                const mesh: THREE.Object3D = loaded?.scene ?? loaded;
                mesh.scale.set(1, 1, 1);
                mesh.rotation.set(0, 0, 0);
                mesh.position.set(0, 0, 0);
                if (currentMeshRef.current) {
                    scene.remove(currentMeshRef.current);
                    currentMeshRef.current.geometry?.dispose?.();
                }
                scene.add(mesh);
                currentMeshRef.current = mesh as THREE.Mesh;

                const bbox = new THREE.Box3().setFromObject(mesh);
                const middle = new THREE.Vector3();
                bbox.getCenter(middle);
                mesh.applyMatrix4(
                    new THREE.Matrix4().makeTranslation(-middle.x, -middle.y, -middle.z),
                );

                if (!controls.autoRotate) {
                    controls.autoRotate = true;
                    controls.autoRotateSpeed = 0.5;
                    const largest = Math.max(bbox.max.x, bbox.max.y, bbox.max.z);
                    camera.position.z = largest * 1.7;
                    camera.position.x = largest * 1.2;
                    camera.position.y = largest * 1.2;
                }
            },
            (xhr: ProgressEvent) => {
                if (thisToken !== loadTokenRef.current || !xhr.total) return;
                const pct = Math.ceil((xhr.loaded / xhr.total) * 100);
                setProgress(pct);
                onProgress?.(pct);
            },
            (err: unknown) => {
                console.error(`Failed to load ${model}:`, err);
            },
        );
    }, [model, format, onProgress]);

    return (
        <div
            ref={containerRef}
            style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}
        >
            <div>
                {model !== undefined && progress < 100 ? (
                    <>
                        <Spinner animation="border" variant="warning" size="sm" /> Loading{" "}
                        {(format ?? (model.endsWith(".stl") ? "stl" : "obj")).toUpperCase()} {progress}%
                    </>
                ) : model === undefined ? (
                    "No model."
                ) : null}
                <br />
                <br />
            </div>
        </div>
    );
}
